import React, { useState } from 'react';
import './Explainability.css';

/**
 * Audit Dashboard
 *
 * Consolidated audit view for compliance officers showing all
 * evaluations with decisions, confidence, integrity status,
 * and export capabilities.  All labels use business language.
 */
const AuditDashboard = ({
  evaluations = [],
  loading = false,
  onExportCompliance,
  onVerifyIntegrity,
  onViewEvaluation,
  onFilterChange,
}) => {
  const [filters, setFilters] = useState({
    analyst: '',
    dateFrom: '',
    dateTo: '',
    decisionType: '',
    search: '',
  });
  const [integrityResults, setIntegrityResults] = useState({});

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    if (onFilterChange) onFilterChange(newFilters);
  };

  const handleVerifyIntegrity = async (evalItem) => {
    if (onVerifyIntegrity) {
      const result = await onVerifyIntegrity(evalItem.trace_id || evalItem.execution_id);
      setIntegrityResults(prev => ({
        ...prev,
        [evalItem.execution_id]: result,
      }));
    }
  };

  const handleClearFilters = () => {
    setFilters({ analyst: '', dateFrom: '', dateTo: '', decisionType: '', search: '' });
    if (onFilterChange) onFilterChange({ analyst: '', dateFrom: '', dateTo: '', decisionType: '', search: '' });
  };

  const filteredEvaluations = evaluations.filter(e => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const query = (e.query || '').toLowerCase();
      const decision = (e.decision || '').toLowerCase();
      if (!query.includes(search) && !decision.includes(search)) return false;
    }
    if (filters.decisionType && e.decision !== filters.decisionType) return false;
    if (filters.dateFrom && e.created_at) {
      if (new Date(e.created_at) < new Date(filters.dateFrom)) return false;
    }
    if (filters.dateTo && e.created_at) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (new Date(e.created_at) > toDate) return false;
    }
    return true;
  });

  const uniqueDecisions = [...new Set(evaluations.map(e => e.decision).filter(Boolean))];

  return (
    <div className="explain-audit-dashboard">
      <h3 className="explain-section-title">Audit Dashboard</h3>

      {/* Filters */}
      <div className="explain-audit-filters">
        <input
          type="text"
          placeholder="Search evaluations..."
          value={filters.search}
          onChange={e => handleFilterChange('search', e.target.value)}
          className="explain-filter-input"
        />
        <select
          value={filters.decisionType}
          onChange={e => handleFilterChange('decisionType', e.target.value)}
          className="explain-filter-select"
        >
          <option value="">All Decisions</option>
          {uniqueDecisions.map((d, i) => (
            <option key={i} value={d}>{d}</option>
          ))}
        </select>
        <input
          type="date"
          value={filters.dateFrom}
          onChange={e => handleFilterChange('dateFrom', e.target.value)}
          className="explain-filter-input explain-filter-input--date"
          placeholder="From"
        />
        <input
          type="date"
          value={filters.dateTo}
          onChange={e => handleFilterChange('dateTo', e.target.value)}
          className="explain-filter-input explain-filter-input--date"
          placeholder="To"
        />
        {(filters.search || filters.decisionType || filters.dateFrom || filters.dateTo) && (
          <button className="explain-btn explain-btn--sm" onClick={handleClearFilters}>
            Clear Filters
          </button>
        )}
      </div>

      {/* Stats bar */}
      <div className="explain-audit-stats">
        <div className="explain-audit-stat">
          <span className="explain-audit-stat-value">{filteredEvaluations.length}</span>
          <span className="explain-audit-stat-label">Evaluations</span>
        </div>
        <div className="explain-audit-stat">
          <span className="explain-audit-stat-value">
            {filteredEvaluations.filter(e => integrityResults[e.execution_id]?.verified).length}
          </span>
          <span className="explain-audit-stat-label">Verified</span>
        </div>
      </div>

      {/* Evaluations table */}
      {loading ? (
        <div className="explain-loading">Loading evaluations...</div>
      ) : (
        <table className="explain-audit-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Query</th>
              <th>Decision</th>
              <th>Confidence</th>
              <th>Integrity</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEvaluations.map((evalItem, i) => {
              const integrity = integrityResults[evalItem.execution_id];
              return (
                <tr key={evalItem.execution_id || i}>
                  <td>{evalItem.created_at ? new Date(evalItem.created_at).toLocaleString() : '-'}</td>
                  <td className="explain-audit-query">
                    {(evalItem.query || '').substring(0, 80)}
                    {(evalItem.query || '').length > 80 ? '...' : ''}
                  </td>
                  <td><strong>{evalItem.decision || '-'}</strong></td>
                  <td>{evalItem.confidence || '-'}</td>
                  <td>
                    {integrity ? (
                      <span className={`explain-integrity-badge ${integrity.verified ? 'explain-integrity--verified' : 'explain-integrity--failed'}`}>
                        {integrity.verified ? 'Verified' : 'Failed'}
                      </span>
                    ) : (
                      <button
                        className="explain-btn explain-btn--sm"
                        onClick={() => handleVerifyIntegrity(evalItem)}
                      >
                        Verify
                      </button>
                    )}
                  </td>
                  <td>
                    <div className="explain-audit-actions">
                      {onViewEvaluation && (
                        <button
                          className="explain-btn explain-btn--sm"
                          onClick={() => onViewEvaluation(evalItem)}
                        >
                          View
                        </button>
                      )}
                      {onExportCompliance && (
                        <button
                          className="explain-btn explain-btn--sm explain-btn--secondary"
                          onClick={() => onExportCompliance(evalItem)}
                        >
                          Export
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {filteredEvaluations.length === 0 && !loading && (
        <div className="explain-empty">No evaluations match the current filters.</div>
      )}
    </div>
  );
};

export default AuditDashboard;
